import React from "react";
import { BRAND } from "../../mock";
import { useLang } from "../../i18n/LangContext";
import {
  LayoutDashboard, Building2, Users, GitBranch, Sparkles, Settings,
  Radar, X, ChevronDown, BookMarked,
} from "lucide-react";

const NAV = [
  { id: "overview", label: "Dashboard", icon: LayoutDashboard },
  { id: "properties", label: "Properties", icon: Building2 },
  { id: "pipeline", label: "Pipeline", icon: GitBranch, children: [
    { id: "pipeline", label: "Property CRM" },
    { id: "agent-pipeline", label: "Agent CRM" },
  ] },
  { id: "agents", label: "Agents", icon: Users },
  { id: "blackbook", label: "Black Book", icon: BookMarked },
  { id: "insights", label: "AI Insights", icon: Sparkles },
  { id: "settings", label: "Settings", icon: Settings },
];

export default function Sidebar({ active, onNavigate, mobileOpen, setMobileOpen }) {
  const { t } = useLang();
  const Item = ({ item }) => {
    const Icon = item.icon;
    const isActive = active === item.id || (item.children && item.children.some((c) => c.id === active));
    if (item.children) {
      return (
        <div className="group/nav relative">
          <button
            onClick={() => { onNavigate(item.children[0].id); }}
            className={`relative flex w-full items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm transition-all ${
              isActive ? "bg-white/[0.06] text-white" : "text-muted-foreground hover:bg-white/[0.03] hover:text-white"
            }`}
          >
            {isActive && <span className="absolute left-0 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-r-full bg-brand-red" />}
            <Icon className={`h-[18px] w-[18px] ${isActive ? "text-brand-red" : ""}`} />
            <span className="font-medium">{t(item.label)}</span>
            <ChevronDown className="ml-auto h-3.5 w-3.5 opacity-60 transition-transform group-hover/nav:rotate-180" />
          </button>
          {/* Hover flyout */}
          <div className="pointer-events-none absolute left-2 right-2 z-20 mt-1 origin-top scale-95 rounded-xl border border-white/10 bg-card p-1 opacity-0 shadow-xl transition-all group-hover/nav:pointer-events-auto group-hover/nav:scale-100 group-hover/nav:opacity-100">
            {item.children.map((c) => (
              <button key={c.id} data-testid={`nav-${c.id}`}
                onClick={() => { onNavigate(c.id); setMobileOpen(false); }}
                className={`flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm transition-colors ${
                  active === c.id ? "bg-white/[0.06] text-white" : "text-muted-foreground hover:bg-white/[0.04] hover:text-white"
                }`}>
                {t(c.label)}
              </button>
            ))}
          </div>
        </div>
      );
    }
    return (
      <button
        onClick={() => { onNavigate(item.id); setMobileOpen(false); }}
        data-testid={`nav-${item.id}`}
        className={`group relative flex w-full items-center gap-3 rounded-xl px-3.5 py-2.5 text-sm transition-all ${
          isActive ? "bg-white/[0.06] text-white" : "text-muted-foreground hover:bg-white/[0.03] hover:text-white"
        }`}
      >
        {isActive && <span className="absolute left-0 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-r-full bg-brand-red" />}
        <Icon className={`h-[18px] w-[18px] transition-colors ${isActive ? "text-brand-red" : "group-hover:text-white"}`} />
        <span className="font-medium">{t(item.label)}</span>
        {item.id === "insights" && (
          <span className="ml-auto rounded-full bg-brand-green/15 px-1.5 py-0.5 text-[10px] font-semibold text-brand-green">4</span>
        )}
      </button>
    );
  };

  const Inner = (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between px-5 py-5">
        <div className="flex items-center gap-3">
          <img src={BRAND.logo} alt="Moroccan Brothers" className="h-14 w-auto max-w-[190px] object-contain" />
        </div>
        <button className="lg:hidden text-muted-foreground" onClick={() => setMobileOpen(false)}><X className="h-5 w-5" /></button>
      </div>

      <nav className="flex-1 space-y-1 px-3">
        {NAV.map((item) => <Item key={item.id} item={item} />)}
      </nav>

      <div className="m-3 rounded-2xl border border-brand-green/20 bg-gradient-to-br from-brand-green/10 to-transparent p-4">
        <div className="flex items-center gap-2 text-brand-green">
          <Radar className="h-4 w-4 animate-pulse-glow" />
          <span className="text-xs font-semibold">{t("AI Radar active")}</span>
        </div>
        <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
          {t("Scanning 1,240 Marrakech listings. 3 new off-market leads today.")}
        </p>
      </div>
    </div>
  );

  return (
    <>
      <aside className="hidden lg:flex w-[256px] shrink-0 border-r border-white/[0.06] bg-card/40">{Inner}</aside>
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setMobileOpen(false)} />
          <div className="absolute left-0 top-0 h-full w-[260px] glass-strong">{Inner}</div>
        </div>
      )}
    </>
  );
}
